/**
 * Données des suites de la confirmation (SPEC CO4, CO5, CO6 ; lib/followUps.ts) : le planning (`class/planning`) du
 * site du cours, semaine du cours et suivante (le même cours la semaine prochaine), et semaines d'avant depuis la
 * semaine courante (prochain cours du coach) : lib/followUps.ts followUpWeeks, 2 semaines quand le cours est cette
 * semaine. La ligne du studio (CO6) reprend les cours lus pour le coach : aucune lecture de plus.
 *
 * Mêmes clés de cache que le planning (api/planning.ts planningQueryKey, site courant, sans coach) : les semaines
 * parcourues pour arriver au cours sont en général déjà là, la suivante sert ensuite au planning. Côté serveur, chaque
 * semaine de chaque site est en cache 5 min pour tous les visiteurs (PlanningService) : pas d'appel Mindbody de plus
 * dans la plupart des cas.
 *
 * Lecture par la route `class/planning` SEULE (fetchPlanningRoute) : jamais de repli sur la route v1 `GET class`
 * (lourde, avec le cookie, hors des routes libérées du verrou de session) ; un 404 abandonne les suites (pas de nouvel
 * essai) et rien n'est écrit sous la clé partagée avec le planning.
 * - v2 allumée par la case du BO (`uxV2.source === 'option'`) : lectures sans le cookie de session (`credentials:
 *   'omit'`, route publique), lancées dès le clic sur « Confirmer » (prefetchFollowUps) : hors du verrou de la session
 *   PHP, elles ne retardent ni `POST class/book` ni les relectures de la confirmation. La réservation dure plusieurs
 *   secondes (4,6 s mesurées le 08/10) : les semaines sont en général arrivées quand la confirmation s'affiche.
 * - Aperçu administrateur (`?ux=v2`, case décochée, `source === 'preview'`) : sans le cookie `mm_ux_preview` et la
 *   session WordPress, le serveur n'a pas la route (404, apps/server/UxV2.php) : lectures AVEC le cookie (la route fait
 *   partie de celles que UxV2SessionRelease libère du verrou), et seulement depuis la confirmation, pas pendant la
 *   réservation (une requête avec le cookie partie avant `POST class/book` le ferait attendre).
 * Arrivée après l'affichage de la confirmation : components/FollowUps n'insère le bloc que s'il ne décale rien de
 * visible.
 */
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import { useMemo } from 'react';
import type { MindbodyClass } from 'types/class';
import type { ClientVisit } from 'types/client';
import { isClassBookable } from 'utils/functions';
import {
  fetchPlanningRoute,
  planningQueryKey,
  PlanningRouteMissing,
  type PlanningClass,
  type PlanningResponse,
} from '../../api/planning';
import { prefilterFromPath, siteKey } from '../../lib/planning/filters';
import { weekStartOf } from '../../lib/planning/week';
import { readPlanningConfig } from '../Planning/config';
import { usePlanningStore } from '../Planning/store';
import {
  coachOffer,
  findSameClassNextWeek,
  followUpWeeks,
  nextWeekOffer,
  studioOffer,
  studioSlotName,
  type CoachOffer,
  type NextWeekOffer,
  type StudioOffer,
  type WeekRef,
} from './lib/followUps';

/** Âge jusqu'auquel une semaine en cache n'est pas relue (= cache partagé du serveur, PlanningService::CACHE_FRESH). */
const FOLLOW_UPS_STALE_MS = 5 * 60 * 1000;
/** = usePlanning : semaines gardées 10 min. */
const FOLLOW_UPS_GC_MS = 10 * 60 * 1000;
/** Autres semaines du cache prises en compte pour le coach : lues depuis moins de 15 min (PlanningService::CACHE_KEEP). */
const CACHED_WEEK_MAX_AGE_MS = 15 * 60 * 1000;

/** Aperçu administrateur (`?ux=v2`) : la route `class/planning` n'existe que pour une requête qui porte le cookie. */
export function isAdminPreview(): boolean {
  return typeof mondayMindbody !== 'undefined' && mondayMindbody?.uxV2?.source === 'preview';
}

function weekQuery(week: WeekRef | undefined, siteId: string) {
  const params = { week: week?.week ?? 0, year: week?.year ?? 0, coachId: null, siteId, currentSiteId: siteId };
  return {
    queryKey: planningQueryKey(params),
    queryFn: () => fetchPlanningRoute(params, { credentials: isAdminPreview() ? 'include' : 'omit' }),
    staleTime: FOLLOW_UPS_STALE_MS,
    gcTime: FOLLOW_UPS_GC_MS,
    // Route absente (404) : suites abandonnées tout de suite ; autre échec : un nouvel essai. Une semaine en échec
    // (lecture anticipée) n'est pas relue à l'arrivée sur la confirmation.
    retry: (failures: number, error: unknown) => !(error instanceof PlanningRouteMissing) && failures < 1,
    retryOnMount: false,
  };
}

const classSiteOf = (cls: Pick<MindbodyClass, 'Location'> | undefined, fallback: string) =>
  String(cls?.Location?.SiteID ?? fallback);

/** Cours dont on propose des suites : du site courant, ni « course » ni inscription à un cycle. */
function followUpsApply(cls: MindbodyClass | undefined, currentSiteId: string): boolean {
  return !!cls && !!currentSiteId && classSiteOf(cls, currentSiteId) === currentSiteId;
}

/**
 * Lecture anticipée des semaines, au clic sur « Confirmer » (fiche cours). Sans effet sur une semaine en cache. Pas en
 * aperçu administrateur (lectures avec le cookie : elles passeraient devant `POST class/book` dans le verrou de session).
 */
export function prefetchFollowUps(queryClient: QueryClient, cls: MindbodyClass | undefined): void {
  if (isAdminPreview()) return;
  const { currentSiteId } = readPlanningConfig();
  if (!followUpsApply(cls, currentSiteId)) return;
  const weeks = followUpWeeks(cls?.StartDateTime, new Date());
  weeks?.forEach((week) => void queryClient.prefetchQuery(weekQuery(week, currentSiteId)));
}

export interface FollowUps {
  /** Les semaines (et les cours du client) sont lues : les suites sont connues. */
  ready: boolean;
  nextWeek: NextWeekOffer | null;
  coach: CoachOffer | null;
  studio: StudioOffer | null;
  /**
   * Nom du studio pour la place réservée (troisième ligne), connu avant les semaines ; null : la ligne du studio ne
   * peut pas apparaître (studio inconnu ou masqué, site à un seul studio d'après le cache), place de deux lignes.
   */
  studioSlot: string | null;
}

const NONE: FollowUps = { ready: true, nextWeek: null, coach: null, studio: null, studioSlot: null };

/**
 * Cours de toutes les semaines du planning en cache (toutes marques, tous âges) lues SANS filtre de coach
 * (planningQueryKey : coachId en 5e position). Une semaine lue pour la page d'un coach (`[mindbody coach]`) ne contient
 * que ses cours : un coach qui n'enseigne que dans un studio ferait croire à un site à un seul studio (place réservée
 * de deux lignes, ligne du studio retenue ; relecture du 09/10). Ces semaines sont aussi restaurées du localStorage
 * (main.tsx) sur la page de réservation ouverte depuis une fiche coach.
 */
export const cachedPlanningClasses = (queryClient: QueryClient): PlanningClass[] =>
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ['v2', 'class/planning'] })
    .filter((q) => q.queryKey[4] === null)
    .flatMap((q) => (q.state.data as PlanningResponse | undefined)?.classes ?? []);

export function useFollowUps(
  cls: MindbodyClass | undefined,
  { visits, visitsReady, isEnrollment }: { visits: ClientVisit[] | undefined; visitsReady: boolean; isEnrollment: boolean },
): FollowUps {
  const config = useMemo(readPlanningConfig, []);
  const { currentSiteId } = config;
  const queryClient = useQueryClient();
  const applies = followUpsApply(cls, currentSiteId);
  const start = cls?.StartDateTime;
  const weeks = useMemo(() => (applies ? followUpWeeks(start, new Date()) : null), [applies, start]);

  /*
   * Un useQuery par semaine possible (semaine du cours, suivante, jusqu'à MAX_EARLIER_WEEKS d'avant), désactivé quand
   * la semaine n'est pas à lire : pas de useQueries, absent de l'entrée commune à la v1 (flag éteint, SPEC § 6.3).
   */
  const classWeek = useQuery({ ...weekQuery(weeks?.[0], currentSiteId), enabled: !!weeks?.[0] });
  const nextWeek = useQuery({ ...weekQuery(weeks?.[1], currentSiteId), enabled: !!weeks?.[1] });
  const earlier1 = useQuery({ ...weekQuery(weeks?.[2], currentSiteId), enabled: !!weeks?.[2] });
  const earlier2 = useQuery({ ...weekQuery(weeks?.[3], currentSiteId), enabled: !!weeks?.[3] });
  const earlier3 = useQuery({ ...weekQuery(weeks?.[4], currentSiteId), enabled: !!weeks?.[4] });
  const read = [classWeek, nextWeek, earlier1, earlier2, earlier3].slice(0, weeks?.length ?? 0);
  // Noms de marque retirés du nom du studio, comme le planning (screens/Planning : marques de `uxV2.brands` et réglage).
  const brandNames = useMemo(
    () => [...config.rawBrands.map((brand) => brand.name ?? ''), config.brandName].map((n) => n.trim()).filter(Boolean),
    [config],
  );
  // Place réservée : nom du studio du cours si sa ligne peut apparaître (cache lu une fois par cours).
  const studioSlot = useMemo(
    () =>
      cls && applies
        ? studioSlotName(cls, cachedPlanningClasses(queryClient), {
            siteId: currentSiteId,
            brandName: config.brandName,
            brandNames,
            lockedCoachId: config.coachId,
            eventSplit: config.eventSplit,
          })
        : null,
    [cls, applies, queryClient, currentSiteId, config, brandNames],
  );
  // Semaine lue : arrivée, en échec, ou encore fraîche. Une semaine plus ancienne (cache du navigateur restauré au
  // chargement) en cours de relecture est attendue : pas de suite proposée sur des places d'il y a une heure.
  const settled = (q: typeof classWeek) =>
    q.isError || (!!q.data && (!q.isFetching || Date.now() - q.dataUpdatedAt < FOLLOW_UPS_STALE_MS));
  const loading = !!weeks && !read.every(settled);

  return useMemo((): FollowUps => {
    if (!cls || !weeks) return NONE;
    if (loading || !visitsReady) return { ready: false, nextWeek: null, coach: null, studio: null, studioSlot };

    const now = new Date();
    const mine = new Set(
      (visits ?? [])
        .filter((v) => !v.LateCancelled && v.AppointmentStatus !== 'Cancelled')
        .map((v) => siteKey(v.SiteId ? String(v.SiteId) : currentSiteId, v.ClassId)),
    );
    const ctx = {
      now,
      siteId: currentSiteId,
      // = utils/functions.ts : mêmes champs lus (StartDateTime, IsCourse) que sur un MindbodyClass complet.
      isBookable: (c: PlanningClass) => isClassBookable(c as unknown as MindbodyClass),
      isMine: (c: PlanningClass) => mine.has(siteKey(String(c.SiteId), c.Id)),
      numberOfDays: config.numberOfDays,
      brandName: config.brandName,
      brandNames,
      // Page coach : pas de ligne vers le planning du studio ni d'un autre coach (lib/followUps.ts lockedCoachId).
      lockedCoachId: config.coachId,
      // Studio Monday Event de Paris séparé (réglage du site) : la ligne du studio vise l'entrée Run Club / Special Events.
      eventSplit: config.eventSplit,
    };

    // « Course » / cycle : pas de « même cours la semaine prochaine » (une inscription couvre toutes ses séances).
    const isCourse = isEnrollment || cls.IsCourse || cls.ClassDescription?.Program?.ScheduleType === 'Enrollment';
    const next = isCourse ? null : findSameClassNextWeek(cls, nextWeek.data?.classes ?? [], currentSiteId);
    const nextOffer = isCourse ? null : nextWeekOffer(cls, next, ctx);

    // Coach et studio : les semaines lues, plus toute autre semaine du site déjà en cache et récente (planning parcouru).
    // Semaines d'une page coach comprises : de vrais cours (un sous-ensemble), qui ne font qu'ajouter des cours du coach.
    const byId = new Map<number, PlanningClass>();
    const add = (list: PlanningClass[] | undefined) => list?.forEach((c) => byId.has(c.Id) || byId.set(c.Id, c));
    [classWeek.data, nextWeek.data, earlier1.data, earlier2.data, earlier3.data]
      .slice(0, weeks.length)
      .forEach((data) => add(data?.classes));
    queryClient
      .getQueryCache()
      .findAll({ queryKey: ['v2', 'class/planning'] })
      .filter((q) => Date.now() - q.state.dataUpdatedAt < CACHED_WEEK_MAX_AGE_MS)
      .forEach((q) => add((q.state.data as PlanningResponse | undefined)?.classes));
    const known = [...byId.values()];
    const proposed = nextOffer ? [nextOffer.cls.Id] : [];
    const coach = coachOffer(cls, known, ctx, proposed);
    const studio = studioOffer(cls, known, ctx, proposed);

    return { ready: true, nextWeek: nextOffer, coach, studio, studioSlot };
  }, [
    cls,
    weeks,
    loading,
    visitsReady,
    visits,
    classWeek.data,
    nextWeek.data,
    earlier1.data,
    earlier2.data,
    earlier3.data,
    isEnrollment,
    currentSiteId,
    config,
    brandNames,
    studioSlot,
    queryClient,
  ]);
}

/**
 * « Réserver un autre cours avec {prénom} » / « … au studio {studio} » : le planning de la route `coach/:id` ou
 * `studio/:id` (pré-filtre PL13 : ce coach ou ce studio, la marque du site, qui est celle du cours) ouvert sur la
 * semaine et le jour du prochain cours proposé (`offer.next`) : vue jour sur ce jour-là, pas un jour vide. L'état du
 * planning est posé avant la navigation, route comprise (`appliedPath`) : le planning le prend tel quel au lieu de
 * réappliquer le pré-filtre sur la semaine gardée en mémoire. Ouverture dans un nouvel onglet (Cmd / Ctrl-clic) : la
 * route seule, semaine courante (comportement de la route).
 */
export function openFollowUpPlanning(offer: Pick<CoachOffer | StudioOffer, 'route' | 'next'>): void {
  const { currentSiteId, eventSplit } = readPlanningConfig();
  const prefilter = prefilterFromPath(offer.route, currentSiteId, [], eventSplit);
  const start = new Date(offer.next.StartDateTime);
  if (!prefilter || Number.isNaN(start.getTime())) return;
  usePlanningStore.setState({
    filters: prefilter.filters,
    weekStart: weekStartOf(start),
    selectedDay: startOfDay(start),
    appliedPath: offer.route,
  });
}
